<template>
  <div class="switch">
    <span v-if="label" class="text-input mb-2 block">
      {{ label }}
    </span>
    <div class="flex gap-x-1">
      <input
        :id="id"
        v-model="value"
        type="checkbox"
        :name="label"
        :data-is-null="value === null ? true : undefined"
        :disabled="disabled"
        class="hidden"
      >
      <label :for="id" class="container w-fit" :class="{ 'has-error': error }">
        <span class="handle" />
        <span class="yes">{{ options ? options.yes : $t('yes') }}</span>
        <span class="no">{{ options ? options.no : $t('no') }}</span>
        <span v-if="nullable" class="null">{{ options ? options.null : $t('null') }}</span>
      </label>
      <button
        v-if="nullable"
        class="null-button"
        type="button"
        @click="value = null"
      >
        ×
      </button>
    </div>
    <span v-if="error" class="error-msg"> {{ error }} </span>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  modelValue?: boolean | null;
  label?: string;
  options?: { yes: string, no: string, null?: string };
  error?: string;
  disabled?: boolean;
  nullable?: boolean;
}

const id = uuidv4();

const emit = defineEmits(['update:modelValue']);
const props = withDefaults(
  defineProps<Props>(),
  { nullable: false },
);
const value = useVModel(props, 'modelValue', emit, { passive: true });
</script>

<style lang="scss" scoped>
.container {
  @apply cursor-pointer;
  @apply flex gap-x-1;
  @apply rounded-4 bg-light-gray;
  @apply p-1;
  @apply relative;
  @apply text-body-regular font-normal;
  @apply select-none;
  transition: background var(--speed-fast) ease;
}
.handle {
  @apply rounded-4 block bg-light-gray;
  @apply absolute top-1 left-1;
  width: calc(1rem + 4px);
  height: calc(1rem + 4px);
  transition:
    transform var(--speed-fast) ease,
    background var(--speed-fast) ease,
    left var(--speed-fast) ease;
}
.null-button {
  @apply pl-1;
  transition:
    opacity var(--speed-fast) ease,
}
.yes {
  @apply text-white;
  opacity: 0;
  transition: opacity var(--speed-fast) ease;
}
.no {
  @apply text-tab-item-gray;
  opacity: 1;
  transition: opacity var(--speed-fast) ease;
}
.null {
  @apply text-tab-item-gray absolute left-0 pl-1;
  opacity: 0;
  transition: opacity var(--speed-fast) ease;
}
input:checked + label {
  &.container {
    @apply bg-success-green;
  }
  .yes {
    opacity: 1;
  }
  .no {
    opacity: 0;
  }
  .null {
    opacity: 0;
  }
  .handle {
    @apply -translate-x-full;
    @apply bg-white;
    left: calc(100% - 4px);
  }
}
input[data-is-null] + label {
  .yes {
    opacity: 0!important;
  }
  .no {
    opacity: 0!important;
  }
  .null {
    opacity: 1!important;
  }
}
input[data-is-null] ~ .null-button {
  opacity: 0;
}

.has-error {
  @apply border border-red-120;
  --input-outline-color: theme('colors.red.120');
}
.error-msg {
  @apply text-red-120;
  font-size: px(10);
  line-height: 1.4em;
}
</style>
